/**
 * Helpers for the total discharge tables (hourly, daily, monthly, annual)
 */

export type DischargePeriod = "hourly" | "daily" | "monthly" | "annual";

export interface AccruedRow {
  accrual_date: any;
  accrued_total: any;
}

export interface TimeWindow {
  start: Date;
  end: Date;
}

/**
 * Converts an accrued total in litres to the widget's display unit
 */
export const toDisplayUnit = (
  litres: number,
  displayUnit: string | undefined,
): number => (displayUnit === "m³" ? litres / 1000 : litres);

/**
 * The browser's IANA time zone, e.g. "Europe/Helsinki". The accrued value
 * functions group by days and hours of this time zone.
 */
export const getBrowserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/**
 * Sums daily accrued totals per day, month or year. Days are local dates
 * ("YYYY-MM-DD") in the time zone passed to hasura_accrued_values, and the
 * returned keys are the first day of each period in the same format.
 */
export const sumAccruedValues = (
  rows: AccruedRow[],
  period: Exclude<DischargePeriod, "hourly">,
  displayUnit: string | undefined,
): Record<string, number> => {
  const sums: Record<string, number> = {};
  rows.forEach(({ accrual_date, accrued_total }) => {
    if (!accrual_date || accrued_total == null) {
      return;
    }
    const [year, month] = String(accrual_date).split("-");
    const key =
      period === "annual"
        ? `${year}-01-01`
        : period === "monthly"
          ? `${year}-${month}-01`
          : String(accrual_date);
    sums[key] = (sums[key] ?? 0) + toDisplayUnit(accrued_total, displayUnit);
  });
  return sums;
};

/**
 * Formats a period key from sumAccruedValues ("YYYY-MM-DD") without time zone
 * conversions: "5.1.2023" (day), "1.2023" (month) or "2023" (year)
 */
export const formatPeriodKey = (
  key: string,
  period: Exclude<DischargePeriod, "hourly">,
): string => {
  const [year, month, day] = key.split("-").map(Number);
  if (period === "annual") {
    return `${year}`;
  }
  if (period === "monthly") {
    return `${month}.${year}`;
  }
  return `${day}.${month}.${year}`;
};

const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const nextLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);

/**
 * Splits [start, end) into pages of one local calendar day each, clipped to
 * the range, so that the hourly table only queries one day at a time.
 */
export const getDayWindows = (start: Date, end: Date): TimeWindow[] => {
  const windows: TimeWindow[] = [];
  if (!(start < end)) {
    return windows;
  }
  let dayStart = startOfLocalDay(start);
  while (dayStart < end) {
    const dayEnd = nextLocalDay(dayStart);
    windows.push({
      start: dayStart < start ? start : dayStart,
      end: dayEnd > end ? end : dayEnd,
    });
    dayStart = dayEnd;
  }
  return windows;
};

const HOUR_MS = 60 * 60 * 1000;

/**
 * Start times of the local clock hours that overlap the window. Hours start
 * on local hour boundaries, which are not whole UTC hours in half-hour time
 * zones (e.g. Australia/Adelaide), and are stepped in absolute time, so
 * daylight saving days have 23 or 25 hours.
 */
export const getHourStarts = (window: TimeWindow): Date[] => {
  const hours: Date[] = [];
  const first = new Date(window.start);
  first.setMinutes(0, 0, 0);
  for (
    let time = first.getTime();
    time < window.end.getTime();
    time += HOUR_MS
  ) {
    hours.push(new Date(time));
  }
  return hours;
};

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * "14:00–15:00" in local time. The end is always the next clock hour, so the
 * hour repeated when clocks go back reads "03:00–04:00" both times.
 */
export const formatHourRange = (hourStart: Date): string => {
  const hour = hourStart.getHours();
  return `${pad(hour)}:00–${pad((hour + 1) % 24)}:00`;
};

/**
 * "5.11.2026" in local time
 */
export const formatLocalDay = (date: Date): string =>
  `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;

/**
 * Hourly totals keyed by the hour's start time in milliseconds
 */
export const sumHourlyValues = (
  rows: { accrual_hour: any; accrued_total: any }[],
  displayUnit: string | undefined,
): Map<number, number> => {
  const sums = new Map<number, number>();
  rows.forEach(({ accrual_hour, accrued_total }) => {
    if (!accrual_hour || accrued_total == null) {
      return;
    }
    const time = new Date(accrual_hour).getTime();
    sums.set(
      time,
      (sums.get(time) ?? 0) + toDisplayUnit(accrued_total, displayUnit),
    );
  });
  return sums;
};
